import { forwardRef } from 'react'
import type { ButtonHTMLAttributes } from 'react'
import { cva } from 'class-variance-authority'
import type { VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { Loader2 } from 'lucide-react'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-[var(--radius-md)] font-medium transition-all duration-200 ease-[var(--ease-smooth)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-amber)]/50 disabled:pointer-events-none disabled:opacity-50 cursor-pointer',
  {
    variants: {
      variant: {
        primary: 'bg-[var(--color-accent-amber)] text-[#1a1714] hover:bg-[var(--color-accent-amber)]/90 shadow-lg shadow-[var(--color-accent-amber)]/10',
        secondary: 'bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)] border border-[var(--color-border-default)] hover:bg-[var(--color-bg-overlay)] hover:border-[var(--color-border-strong)]',
        ghost: 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)]',
        danger: 'bg-[var(--color-accent-red-dim)] text-[var(--color-accent-red)] hover:bg-[var(--color-accent-red)]/25',
        success: 'bg-[var(--color-accent-green-dim)] text-[var(--color-accent-green)] hover:bg-[var(--color-accent-green)]/25',
        link: 'text-[var(--color-accent-amber)] underline-offset-4 hover:underline p-0 h-auto',
      },
      size: {
        sm: 'h-10 px-3 text-xs sm:h-8',
        md: 'h-11 px-4 text-sm sm:h-10',
        lg: 'h-12 px-6 text-base',
        icon: 'h-11 w-11 sm:h-10 sm:w-10',
        'icon-sm': 'h-10 w-10 sm:h-8 sm:w-8',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, children, disabled, ...props }, ref) => (
    <button
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      disabled={disabled || loading}
      type={props.type ?? 'button'}
      {...props}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  ),
)
Button.displayName = 'Button'

export { Button, buttonVariants }
